/* eslint-disable indent */
import { FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { HumanModerationStatus } from '@/api/entities/moderation';
import {
  ActionUserType,
  VehiculeCheckProgressStatus,
} from '@/api/entities/vehicule-check';
import { TableCell } from '@/components/Datatable/TableCell';
import { SubMenuItem } from '@/components/Menu/SubMenuItem';
import { TableMenu } from '@/components/Menu/TableMenu';
import { StatusTag } from '@/components/Tag/StatusTag';
import { useUserRole } from '@/hooks/auth.hooks';
import { IDatatableData } from '@/type/listing.types';
import { getFormattedDate, getFormattedTime } from '@/utils/date.utils';
import {
  getInspectionProgressStatusLabel,
  getInspectionSubProgressStatus,
} from '@/utils/vehicule-check';
import { useSelectedTableItems } from '@/hooks/selected-table-items.hooks';
import { Checkbox } from '@/components/Controls/Checkbox';
import { TableHeaderActions } from '@/components/Custom/Datatable/TableHeaderActions';
import { ViewVehiculeCheckTab } from '@/type/ui/tab/vehiculeCheckTab';
import { ResultPhotoTab } from '@/type/ui/tab/vehiculeCheckResultTab';
import { SmallInfoContent } from '@/components/SmallInfoContent';

export const VehiculeCheckTableData = <TSchema extends FieldValues>({
  t,
  data,
  canSelectItems = true,
  pendingDeactivate,
  onDeactivate,
}: IDatatableData<TSchema>) => {
  const navigate = useNavigate();
  const { isAdmin, isModerator, isGroupManager, isCompanyManager } =
    useUserRole();

  const isRequestListPage = location.pathname.startsWith(
    '/vehicule-check-requests',
  );

  const basePath = isRequestListPage
    ? '/vehicule-check-request'
    : '/vehicule-check';

  const {
    numberOfItemsText,
    selectedTableData,
    onSelectedItem,
    handleDeactivate,
  } = useSelectedTableItems({
    t,
    data,
    onDeactivate,
  });

  if (!data || data.length === 0) {
    return <></>;
  }

  return (
    <>
      {canSelectItems && selectedTableData?.length > 0 && (
        <TableHeaderActions
          colSpan={9}
          text={numberOfItemsText}
          disableDownload
          onDeactivate={isAdmin ? handleDeactivate : undefined}
          onDownload={() => {}}
          onCreate={() => navigate('/vehicule-check-request/create')}
        />
      )}
      {data.map((entry: TSchema) => {
        const licensePlateNumber =
          entry?.vehiculeCheckResult?.licensePlateNumber;
        const vin = entry?.vehiculeCheckResult?.vin;

        const groups = entry?.user?.entityRoles
          ?.filter((role) => role?.group)
          ?.map((role) => role?.group);
        const companies = entry?.user?.entityRoles
          ?.filter((role) => role?.company)
          ?.map((role) => role?.company);
        const branches = entry?.user?.entityRoles
          ?.filter((role) => role?.branch)
          ?.map((role) => role?.branch);

        const groupsExist = !!groups?.length;
        const companiesExist = !!companies?.length;
        const branchesExist = !!branches?.length;

        const isIndividual =
          entry?.actionUserType === ActionUserType.INDIVIDUAL;

        const isToModerate =
          entry?.humanModerationStatus === HumanModerationStatus.A_MODERER;

        const isSubProgressStatusCompleted = entry?.progressStatus
          ?.toLowerCase()
          .includes('completed_');

        const subProgressStatus = getInspectionSubProgressStatus({
          t,
          status: entry?.progressStatus,
        });

        return (
          <tr key={entry?.id}>
            {canSelectItems && !isModerator && (
              <TableCell
                tdClassName="sticky-select-column"
                content={
                  <Checkbox
                    checked={Boolean(
                      selectedTableData?.find((item) => item?.id === entry?.id),
                    )}
                    onChange={() => onSelectedItem(entry?.id)}
                  />
                }
              />
            )}
            <TableCell
              content={
                <div className="flex flex-col">
                  <div className="flex gap-1.5">
                    <div>
                      <i className="-mt-[5.7px] ki-outline ki-calendar text-md" />
                    </div>
                    <div>{getFormattedDate(entry?.updatedAt)}</div>
                  </div>
                  <div className="flex gap-1.5">
                    <div>
                      <i className="-mt-[5.7px] ki-outline ki-time text-md" />
                    </div>
                    <div>{getFormattedTime(entry?.updatedAt)}</div>
                  </div>
                </div>
              }
            />
            <TableCell
              content={
                <>
                  <div className="flex gap-2">
                    <StatusTag
                      text="123"
                      className="self-start px-1.5 py-[1.5px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs font-normal dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                    />
                    <div className="mt-[0.5px] font-medium">
                      {licensePlateNumber || '--'}
                    </div>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <StatusTag
                      text="VIN"
                      className="self-start px-1.5 py-[1.5px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs font-normal dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                    />
                    <div className="mt-[0.5px] break-all">{vin || '--'}</div>
                  </div>
                </>
              }
            />
            <TableCell
              tdClassName="min-w-[135px] text-[13.7px] break-all"
              content={entry?.referenceNumber || '--'}
            />
            <TableCell
              content={
                <div>
                  <div className="font-medium">
                    {entry?.user?.firstname && entry?.user?.lastname
                      ? `${
                          entry?.user?.firstname
                        } ${entry?.user?.lastname?.toUpperCase()}`
                      : '--'}
                  </div>
                  {(groupsExist || companiesExist || branchesExist) && (
                    <div className="mt-1.5 flex flex-col gap-1">
                      {(isAdmin || isModerator || isGroupManager) &&
                        groupsExist && (
                          <SmallInfoContent
                            label={
                              groups?.length > 1
                                ? t('common:groups')
                                : t('common:group')
                            }
                            value={groups
                              ?.map((group) => group?.name)
                              ?.join(', ')}
                          />
                        )}
                      {(isAdmin || isModerator || isCompanyManager) &&
                        companiesExist && (
                          <SmallInfoContent
                            label={
                              companies?.length > 1
                                ? t('common:companies')
                                : t('common:company')
                            }
                            value={companies
                              ?.map((company) => company?.name)
                              ?.join(', ')}
                          />
                        )}
                      {branchesExist && (
                        <SmallInfoContent
                          label={
                            branches?.length > 1
                              ? t('common:branches')
                              : t('common:branch')
                          }
                          value={branches
                            ?.map((branch) => branch?.name)
                            ?.join(', ')}
                        />
                      )}
                    </div>
                  )}
                </div>
              }
            />
            <TableCell
              content={
                <div className="flex justify-center items-center">
                  <div className="px-3 py-2.5 min-w-[62px] bg-slate-50 rounded-lg font-semibold text-[13.3px] tracking-wide text-center uppercase">
                    {isIndividual
                      ? t('common:individual_short')
                      : t('common:professional_short')}
                  </div>
                </div>
              }
            />
            <TableCell
              tdClassName="!px-2.5 text-center"
              content={
                entry?.progressStatus
                  ?.toLowerCase()
                  ?.startsWith('completed') ? (
                  <>
                    <StatusTag
                      text={`${
                        isToModerate
                          ? t('common:toModerate')
                          : t('common:moderated_feminine')
                      }`}
                      className={twMerge(
                        'flex justify-center items-center pt-[6px] pb-[5px] text-white text-[10.1px] font-semibold [word-spacing:1.9px] leading-tight tracking-[0.01em] uppercase',
                        isToModerate &&
                          'bg-red-FFEEF4 border-red-FFCAD6 text-red-FF3680',
                        !isToModerate &&
                          'bg-green-EAFFF2 border-green-C9F4D2 text-green-11BFA4',
                      )}
                    />
                    {!isToModerate && (
                      <StatusTag
                        text={`${
                          entry?.vehiculeCheckResult?.reportValidated
                            ? t('common:validated_feminine')
                            : t('common:toValidate')
                        }`}
                        className={twMerge(
                          'mt-1.75 flex justify-center items-center pt-[6px] pb-[5px] text-white text-[10.1px] font-semibold [word-spacing:1.9px] leading-tight tracking-[0.01em] uppercase',
                          entry?.vehiculeCheckResult?.reportValidated &&
                            'bg-green-11BFA4',
                          !entry?.vehiculeCheckResult?.reportValidated &&
                            'bg-orange-FF6D00/5 border-orange-FF6D00/20 text-orange-FF6D00',
                        )}
                      />
                    )}
                  </>
                ) : (
                  '--'
                )
              }
            />
            <TableCell
              tdClassName="!px-2.5 text-center"
              content={
                <>
                  <StatusTag
                    className="inline-flex justify-center items-center pt-[6.75px] pb-[5.75px] border-indigo-600/30 bg-purple-EFEFFD text-indigo-600 text-[10.1px] font-semibold [word-spacing:1.9px] leading-tight uppercase"
                    text={
                      getInspectionProgressStatusLabel({
                        t,
                        status: entry?.progressStatus,
                      }) || '--'
                    }
                  />
                  {isSubProgressStatusCompleted && (
                    <div
                      className={twMerge(
                        'mt-0.75 text-xs leading-tight font-medium',
                        subProgressStatus?.className,
                      )}
                    >
                      {subProgressStatus?.text}
                    </div>
                  )}
                </>
              }
            />
            <TableCell
              tdClassName="sticky-action-column"
              content={
                <TableMenu disabled={pendingDeactivate}>
                  {!isAdmin && isModerator && (
                    <SubMenuItem
                      text="Modérer"
                      leftIconClassName="ki-toggle-on !text-[17.5px]"
                      to={`${basePath}/view/${entry?.id}?tab=${ViewVehiculeCheckTab.DETAILED_RESULTS}&childTab=${ResultPhotoTab.EQUIPMENTS}`}
                    />
                  )}
                  {!isModerator && (
                    <>
                      <SubMenuItem
                        text={t('common:view')}
                        leftIconClassName="ki-eye !text-[17.5px]"
                        to={`${basePath}/view/${entry?.id}`}
                      />
                      {isIndividual && (
                        <SubMenuItem
                          text={
                            entry?.progressStatus?.includes(
                              VehiculeCheckProgressStatus.COMPLETED,
                            )
                              ? t('viewRequest')
                              : t('common:changeRequest')
                          }
                          leftIconClassName="ki-paper-plane !text-[16px]"
                          to={`/vehicule-check-request/${entry?.id}`}
                        />
                      )}
                      <SubMenuItem
                        text={t('common:edit')}
                        leftIconClassName="ki-notepad-edit !text-[16px]"
                        to={`${basePath}/edit/${entry?.id}`}
                      />
                      {isAdmin && (
                        <SubMenuItem
                          text={t('common:delete')}
                          disabled={pendingDeactivate}
                          leftIconClassName="ki-trash !text-[19px]"
                          textClassName="line-clamp-1"
                          onClick={() => onDeactivate([entry])}
                        />
                      )}
                    </>
                  )}
                </TableMenu>
              }
            />
          </tr>
        );
      })}
    </>
  );
};
